// Chakra v3 only: index.html inlines the v3 token and global CSS alone, and
// the pre-rendered markup must paint before Chakra v2 loads.
import { Box, Flex, HStack, type BoxProps } from "@chakra-ui/react-v3";
import { useLayoutEffect, useRef } from "react";
import LogoMini from "assets/logos/tigerdata-logo-mini.svg?react";
// Importing this module also records the pre-rendered shell's shimmer phase
// (see ShimmerInPhase), so it must load before React's first render.
import { ShimmerInPhase, Skeleton } from "components/ui/skeleton";

import { getBootShellKind, NO_TABS_PATH } from "./bootShellKind";

// Matches the horizontal padding of MainNav and the project pages.
const PAGE_PX = { base: "4", md: "8" };

// The keyframes of the public shell's logo: its delayed fade-in and its pulse.
const LOGO_KEYFRAMES = ["fade-in", "pulse"];

const logoKeyframes = (animation: Animation) =>
  animation instanceof CSSAnimation &&
  LOGO_KEYFRAMES.includes(animation.animationName)
    ? animation.animationName
    : null;

// Like ShimmerInPhase for the skeletons: the public shell is replaced several
// times before the page arrives (index.html, then AuthProvider and the
// router's first load), and each new logo would restart its animations,
// vanishing to wait out the delay again. So each adopts one start time per
// animation: the pre-rendered logo's, read while it is still on screen, as
// this module loads before React's first render.
const logoStartTimes: Partial<Record<string, CSSNumberish | null>> = {};
if (typeof document !== "undefined" && "getAnimations" in document) {
  for (const animation of document.getAnimations()) {
    const keyframes = logoKeyframes(animation);
    if (keyframes) logoStartTimes[keyframes] ??= animation.startTime;
  }
}

const useLogoInPhase = () => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    for (const animation of ref.current?.getAnimations?.({ subtree: true }) ??
      []) {
      const keyframes = logoKeyframes(animation);
      if (!keyframes) continue;

      // Nothing to follow yet: no pre-rendered public shell (the boot showed
      // the dashboard one, or the dev server rendered none). The animation
      // hasn't started, and assigning a null start time would pause it, so
      // let it run and lead the logos that mount later.
      const startTime = logoStartTimes[keyframes] ?? null;
      if (startTime === null) {
        animation.ready
          .then(({ startTime }) => {
            logoStartTimes[keyframes] ??= startTime;
          })
          // Rejects when the shell unmounts before the animation starts.
          .catch(() => {});
        continue;
      }

      animation.startTime = startTime;
    }
  }, []);

  return ref;
};

const Logo = (props: BoxProps) => (
  <Box
    asChild
    fill="fg.default"
    h="8"
    w="8"
    flexShrink="0"
    aria-hidden
    {...props}
  >
    <LogoMini />
  </Box>
);

// No `window` while pre-rendering: keep the tab row and let the
// `BOOT_SHELL_TABS_ATTRIBUTE` rule in index.html hide it by its
// `data-boot-shell-tabs` marker.
const hasTabRow = () =>
  typeof window === "undefined" || !NO_TABS_PATH.test(window.location.pathname);

/**
 * Loading frame for the signed-in dashboard: MainNav's header and tab row over
 * an empty page. Rendered by the router's `authLayout` pending state and
 * `BootShell`, and pre-rendered into index.html, so a change here changes all
 * of those loading states. The nav's geometry matches the real one, so the
 * swap doesn't shift anything. Pages draw their own placeholders as their
 * routes' `pendingComponent`s once the real nav is on screen.
 */
export const DashboardShell = () => (
  <ShimmerInPhase>
    <Flex
      direction="column"
      h="100vh"
      overflow="hidden"
      bg="bg.default"
      aria-busy="true"
    >
      <Box
        bg="bg.container"
        borderBottomWidth="1px"
        borderColor="border.container"
        flexShrink="0"
      >
        <HStack h="15" px={PAGE_PX} gap="4">
          <Logo />
          <Skeleton height="6" width="48" />
          <HStack gap="2" ms="auto">
            <Skeleton height="9" width="52" />
            <Skeleton height="9" width="20" />
          </HStack>
        </HStack>
        {hasTabRow() && (
          <HStack h="9" px={PAGE_PX} gap="6" data-boot-shell-tabs="">
            {(["14", "14", "16", "12", "14"] as const).map((width, index) => (
              <Skeleton key={index} height="4" width={width} />
            ))}
          </HStack>
        )}
      </Box>
    </Flex>
  </ShimmerInPhase>
);

/**
 * Loading frame for public and standalone pages (login, signup, verify,
 * OAuth, …): the logo centred on the page background. These pages share no
 * common frame, so the shell doesn't guess at one.
 *
 * The logo fades in after a delay, so a fast load goes straight from the empty
 * page to the real one instead of flashing it, then pulses while loading.
 */
export const PublicShell = () => {
  const ref = useLogoInPhase();

  return (
    <Flex
      ref={ref}
      minH="100vh"
      align="center"
      justify="center"
      bg="bg.default"
      aria-busy="true"
    >
      {/* Two elements: the delayed fade-in and the pulse both animate opacity. */}
      <Flex animation="fade-in {durations.fast} {easings.easeOut} {durations.extraSlow} both">
        <Logo
          animation="pulse {durations.pulse} {easings.easeInOut} infinite"
          _motionReduce={{ animation: "none" }}
        />
      </Flex>
    </Flex>
  );
};

/**
 * The shell chosen before first paint by the inline boot script, for loading
 * states that render before the router knows the route (e.g. `AuthProvider`).
 */
export const BootShell = () =>
  getBootShellKind() === "dashboard" ? <DashboardShell /> : <PublicShell />;
